import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';

interface Props {
  label: string;
  summary: ReactNode;
  id?: string;
  className?: string;
  panelClassName?: string;
  onToggle?: (open: boolean) => void;
  children: ReactNode;
}

/**
 * A small drop-down on <details>: closes on a click outside, on Escape, or when an item is chosen.
 * The label names the button for screen readers and as its tooltip (some show only an icon).
 */
export function Menu({
  label,
  summary,
  id,
  className = '',
  panelClassName = '',
  onToggle,
  children,
}: Props) {
  const ref = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      const d = ref.current;
      if (d?.open && !d.contains(e.target as Node)) d.open = false;
    };
    const onKey = (e: KeyboardEvent) => {
      const d = ref.current;
      if (e.key !== 'Escape' || !d?.open) return;
      d.open = false;
      d.querySelector('summary')?.focus();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const closeOnChoice = (e: MouseEvent) => {
    if (ref.current && (e.target as Element).closest('button, a')) ref.current.open = false;
  };

  return (
    <details
      ref={ref}
      id={id}
      className={`menu ${className}`}
      onToggle={(e) => onToggle?.(e.currentTarget.open)}
    >
      <summary aria-label={label} title={label}>
        {summary}
      </summary>
      <div className={`menu__panel ${panelClassName}`} onClick={closeOnChoice}>
        {children}
      </div>
    </details>
  );
}
